"use client";

import DashboardHeader from "@/components/dashboard-header";
import UserManagement from "@/components/user-management";
import StudentImport from "@/components/admin/student-import";
import StudentColumnsReference from "@/components/admin/student-columns-reference";
import FacultyMajorManagement from "@/components/admin/faculty-major-management";
import PaperManagement from "@/components/admin/paper-management";
import AppSettingsManagement from "@/components/admin/app-settings-management";
import HocPhiConfigManagement from "@/components/admin/hocphi-config-management";
import { useAuth } from "@/global-hooks/useAuth";
import { Navigate } from "react-router-dom";
import { useUrlParams } from "@/hooks/use-url-params";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import statisticsAPIService from "@/lib/statistics-api.service";

/** Mọi tab hợp lệ. Tham số lạ trên URL rơi về phần tử đầu tiên. */
const TABS = ["users", "import", "export", "faculties", "papers", "columns", "settings"] as const;

const TAB_PARAM = "tab";

export default function AdminPage() {
	const auth = useAuth();
	// Controlled theo URL để mỗi tab quản trị có link đi thẳng tới nó — nhất là
	// "Cài Đặt", chỗ hay phải chỉ cho người khác vào.
	const [urlParams, setUrlParams] = useUrlParams();
	const tabParam = urlParams.get(TAB_PARAM);
	const activeTab =
		tabParam !== null && (TABS as readonly string[]).includes(tabParam) ? tabParam : TABS[0];

	// Check if user has admin role
	if (!auth.user || auth.user.role !== "admin") {
		return <Navigate to="/dashboard" replace />;
	}

	return (
		<div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-950 dark:to-indigo-900">
			<DashboardHeader />

			<div className="container mx-auto p-6">
				<div className="bg-card rounded-lg shadow-sm p-6">
					<h1 className="text-2xl font-bold text-foreground mb-6">Quản Trị Hệ Thống</h1>

					<Tabs
						value={activeTab}
						onValueChange={(tab) => setUrlParams({ [TAB_PARAM]: tab })}
						className="w-full"
					>
						{/* Seven equal columns squash on mobile, so they collapse to fewer
							columns below the lg breakpoint instead of shrinking in place.
							Row height and the gap between tabs come from TabsList itself. */}
						<TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 lg:grid-cols-7">
							<TabsTrigger value="users">Quản Lý Người Dùng</TabsTrigger>
							<TabsTrigger value="import">Nhập Dữ Liệu</TabsTrigger>
							<TabsTrigger value="export">Xuất Dữ Liệu</TabsTrigger>
							<TabsTrigger value="faculties">Khoa & Ngành</TabsTrigger>
							<TabsTrigger value="papers">Hồ Sơ</TabsTrigger>
							<TabsTrigger value="columns">Tra Cứu Cột</TabsTrigger>
							<TabsTrigger value="settings">Cài Đặt</TabsTrigger>
						</TabsList>

						<TabsContent value="users" className="mt-6">
							<UserManagement />
						</TabsContent>

						<TabsContent value="import" className="mt-6">
							<StudentImport />
						</TabsContent>

						<TabsContent value="faculties" className="mt-6">
							<FacultyMajorManagement />
						</TabsContent>

						<TabsContent value="papers" className="mt-6">
							<PaperManagement />
						</TabsContent>

						<TabsContent value="columns" className="mt-6">
							<StudentColumnsReference />
						</TabsContent>

						<TabsContent value="settings" className="mt-6">
							<div className="space-y-6">
								<AppSettingsManagement />
								<HocPhiConfigManagement />
							</div>
						</TabsContent>

						<TabsContent value="export" className="mt-6">
							<div className="space-y-6">
								<div className="bg-muted rounded-lg p-6">
									<h3 className="text-lg font-semibold text-foreground mb-4">Xuất Dữ Liệu CSV</h3>
									<p className="text-muted-foreground mb-6">
										Xuất dữ liệu sinh viên dưới dạng CSV để sử dụng cho các mục đích khác
									</p>
									<div className="flex flex-wrap gap-4">
										<Button
											onClick={async () => {
												try {
													await statisticsAPIService.exportAllStudentsAsCSV();
												} catch (error) {
													console.error("Error exporting all students CSV:", error);
													alert("Không thể tải CSV toàn bộ dữ liệu. Vui lòng thử lại.");
												}
											}}
											variant="outline"
											className="flex items-center gap-2"
										>
											<Download className="h-4 w-4" />
											Tải toàn bộ dữ liệu CSV
										</Button>
										<Button
											onClick={async () => {
												try {
													await statisticsAPIService.exportReportAsCSV();
												} catch (error) {
													console.error("Error exporting report CSV:", error);
													alert("Không thể tải báo cáo CSV. Vui lòng thử lại.");
												}
											}}
											variant="outline"
											className="flex items-center gap-2"
										>
											<Download className="h-4 w-4" />
											Tải báo cáo
										</Button>
									</div>
								</div>
							</div>
						</TabsContent>
					</Tabs>
				</div>
			</div>
		</div>
	);
}
